import type { ColumnDef } from '@tanstack/react-table';

import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useDynamicLocale } from '@/hoslog-intl';
import type { SupportedLocale } from '@/hoslog-intl/Translation';
import { EyeIcon } from '@/icons';
import { formatDate } from '@/lib/dateFormatter';
import { formatAmount } from '@/lib/format';
import type { StorePaymentLogsData } from '@/services/payment/payment.types';
import { Link } from '@tanstack/react-router';

declare module '@tanstack/react-router' {
  interface HistoryState {
    storeName?: string;
    storeNameJp?: string;
  }
}

export function getColumns(
  t: ReturnType<typeof useDynamicLocale>['t'],
  locationOptions: { label: string; value: string }[] = [],
  locale?: SupportedLocale,
): ColumnDef<StorePaymentLogsData>[] {
  return [
    {
      id: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="font-medium text-caption text-foreground truncate max-w-[10rem]">
          {getSerialNumber(table, row.index)}
        </div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'storeName',
      id: 'storeInfo',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.storeInfo')} />,
      cell: ({ row }) => {
        const { storeName, storeInfo, storeNameJp, storeLogo } = row.original;
        const name = storeName || storeInfo;
        const storeId = row.original.clubId || row.original.id;

        return (
          <div className="flex items-center space-x-3 max-w-[20rem] truncate">
            <img
              src={storeLogo?.path || '/no-image.jpeg'}
              alt={storeLogo?.alt || storeLogo?.description || 'logo'}
              title={storeLogo?.description || storeLogo?.alt}
              className="w-8 h-8 shrink-0 rounded-full object-cover bg-surface-sunken"
              onError={(e) => {
                const target = e.currentTarget;
                if (!target.src.endsWith('/no-image.jpeg')) {
                  target.src = '/no-image.jpeg';
                }
              }}
            />
            <div className="truncate">
              <Can
                rule={{ allOf: [PERMS.paymentManagement.storePaymentLogs.details] }}
                fallback={<div className="font-medium text-caption text-link underline">{name}</div>}
              >
                <Link
                  to="/payment-management/store-payment-log/$storeId"
                  params={{ storeId: String(storeId) }}
                  search={{ storeName: storeName || '', storeNameJp: storeNameJp || '' }}
                  state={{ storeName, storeNameJp }}
                >
                  <div className="font-medium text-caption text-link underline">{name}</div>
                </Link>
              </Can>
              {storeNameJp && <div className="font-medium text-caption text-foreground-subtle">{storeNameJp}</div>}
            </div>
          </div>
        );
      },
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.storeInfo',
      },
    },
    {
      accessorKey: 'prefecture',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.prefecture')} />,
      cell: ({ row }) => {
        // API returns "<japanese>,<english>"
        const [jp, en] = String(row.getValue('prefecture') ?? '').split(',');
        const prefecture = (locale === 'en' ? en || jp : jp || en)?.trim() || '-';
        return <div className="text-caption text-foreground">{prefecture}</div>;
      },
      meta: {
        label: t('common.page.tableColumn.prefecture'),
        options: locationOptions,
      },
      enableColumnFilter: true,
    },
    {
      accessorKey: 'totalBookings',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.totalBookings')} />
      ),
      cell: ({ row }) => <div className="text-caption text-foreground">{row.getValue('totalBookings')}</div>,
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.totalBookings',
      },
    },
    {
      id: 'createdDate',
      accessorKey: 'visitDateTime',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.storeVisitDateAndTime')} />
      ),
      cell: ({ row }) => {
        const visitDateTime = row.original.visitDateTime;
        const formattedDate = visitDateTime ? formatDate(visitDateTime, 'YYYY/MM/DD HH:mm:ss') : '-';
        return <div className="text-caption text-foreground truncate">{formattedDate}</div>;
      },
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.storeVisitDateAndTime',
      },
      enableColumnFilter: true,
    },
    {
      accessorKey: 'totalPlanAmount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.totalPlanAmount')} />
      ),
      cell: ({ row }) => (
        <div className="text-caption text-right text-foreground truncate pr-3">
          {formatAmount(row.getValue('totalPlanAmount'))}
        </div>
      ),
      meta: { align: 'right', hideColumnLabelKey: 'common.page.tableColumn.totalPlanAmount' },
    },
    {
      accessorKey: 'commissionAmount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.commissionAmount')} />
      ),
      cell: ({ row }) => (
        <div className="text-caption text-right text-foreground truncate pr-3">
          {formatAmount(row.getValue('commissionAmount'))}
        </div>
      ),
      meta: { align: 'right', hideColumnLabelKey: 'common.page.tableColumn.commissionAmount' },
    },
    {
      accessorKey: 'totalAmount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.totalAmount')} />
      ),
      cell: ({ row }) => (
        <div className="text-caption text-right text-foreground truncate pr-3">
          {formatAmount(row.getValue('totalAmount'))}
        </div>
      ),
      meta: { align: 'right', hideColumnLabelKey: 'common.page.tableColumn.totalAmount' },
    },
    {
      id: 'actions',
      accessorKey: 'Actions',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.actions')} />,
      cell: ({ row }) => {
        const storeId = row.original.clubId || row.original.id;
        const { storeName, storeNameJp } = row.original;
        return (
          <Can rule={{ allOf: [PERMS.paymentManagement.storePaymentLogs.details] }}>
            <Link
              to="/payment-management/store-payment-log/$storeId"
              params={{ storeId: String(storeId) }}
              search={{ storeName: storeName || '', storeNameJp: storeNameJp || '' }}
              state={{ storeName, storeNameJp }}
            >
              <EyeIcon className="stroke-warning size-4" />
            </Link>
          </Can>
        );
      },
      size: 40,
      enableSorting: false,
      enableHiding: false,
    },
  ];
}
